import baseStyles from "../../toolbox.module.css";
import styles from "./toolbox-line-style.module.css";
import { ChangeEvent, useEffect } from "react";
import {
  ToolboxComponentSection as Section,
  ToolboxSectionDivider as Divider,
} from "../../toolbox-components/toolbox-component-section";
import { BasicToolboxProps, Toolbox } from "../../toolbox";
import { usePosterProject } from "PosterEditor/model/poster-project-provider";
import { ToolboxComponentSlider } from "../../toolbox-components/toolbox-component-slider";
import { ToolboxComponentColorPicker } from "../../toolbox-components/toolbox-component-color-picker";
import { ToolboxComponentToggle } from "../../toolbox-components/toolbox-component-toggle";
import { ToolboxComponentCheckbox } from "../../toolbox-components/toolbox-component-checkbox";
import {
  ThumbnailGrid,
  Thumbnail,
} from "../../toolbox-components/toolbox-component-thumbnail-grid";
import { pickerColors } from "PosterEditor/model/poster-project";
import { useState } from "react";
import { LinePattern } from "PosterEditor/model/poster-types";
import { UIIconName } from "PosterEditor/common-components/ui-icon-drawing/ui-icon-type";
import { UIIconButton } from "PosterEditor/common-components/ui-icon-drawing/ui-icon-button";
import { convertToRGBA } from "PosterEditor/utils/color-utils";
import { Shadow } from "PosterEditor/model/poster-types";
import { ToolboxComponentSection } from "../../toolbox-components/toolbox-component-section";
import { defaultShadow } from "PosterEditor/model/default-project-style";
import { RouteSourceSection } from "./route-source-section";
import { useAnalyticsTracker } from "analytics-tracker";

interface ToolboxLineStyleProps extends BasicToolboxProps {}

export const ToolboxLineStyle = (props: ToolboxLineStyleProps) => {
  const { gpsSource } = usePosterProject();

  return (
    <Toolbox
      key={"line-style"}
      title="Line style"
      collapsible={props.collapsible}
      isCollapsed={props.isCollapsed}
      onToggleCollapse={props.onToggleCollapse}
    >
      <div className={baseStyles.paddedBodyContent}>
        <RouteSourceSection />
        <Divider />
        {gpsSource?.id && <LineStyleSection />}
      </div>
    </Toolbox>
  );
};

interface LineStyleSectionProps {}

const LineStyleSection = (props: LineStyleSectionProps) => {
  const { isTemplateEditing, project, setEditingPermissions } =
    usePosterProject();
  return (
    <>
      {" "}
      {(isTemplateEditing || project.editingPermissions.line.dash) && (
        <>
          <Section>
            <LinePatternControl />
          </Section>{" "}
        </>
      )}
      {(isTemplateEditing || project.editingPermissions.line.color) && (
        <>
          <Divider />
          <Section>
            <LineColor />
          </Section>
        </>
      )}
      {(isTemplateEditing || project.editingPermissions.line.thickness) && (
        <>
          <Divider />
          <Section>
            <LineWidth />
          </Section>
        </>
      )}
      {/* {(isTemplateEditing || project.editingPermissions.line.shadow) && ( */}
      <>
        <Divider />
        <Section title="Shadow">
          <LineShadow />
        </Section>
      </>
      {/* )} */}
      {(isTemplateEditing || project.editingPermissions.line.pointDensity) && (
        <>
          <Divider />
          <Section>
            <DensitySlider />
          </Section>
        </>
      )}
    </>
  );
};

interface LineColorProps {}

const LineColor = (props: LineColorProps) => {
  const { project, setMainRouteColor, setFlankRouteColor } = usePosterProject();
  const [isMainColorExpanded, setIsMainColorExpanded] =
    useState<boolean>(false);
  const [isSecondaryColorExpanded, setIsSecondaryColorExpanded] =
    useState<boolean>(false);

  return (
    <div className={styles.lineColorPickersContainer}>
      <div className={styles.pickerColorswatches}>
        <ToolboxComponentColorPicker
          title="Color"
          showSwatch={true}
          colors={pickerColors}
          selectedColor={project.lineStyle.mainColor}
          onHoverColor={() => {}}
          onSelectColor={setMainRouteColor}
          isExpanded={isMainColorExpanded}
          onToggleExpand={() => {
            setIsMainColorExpanded(!isMainColorExpanded);
          }}
        />

        {project.lineStyle.mainPattern !== "singleSolid" && (
          <ToolboxComponentColorPicker
            title="Outside color"
            showSwatch={true}
            colors={pickerColors}
            selectedColor={
              project.lineStyle.flankColor ?? project.lineStyle.mainColor
            }
            onHoverColor={() => {}}
            onSelectColor={setFlankRouteColor}
            isExpanded={isSecondaryColorExpanded}
            onToggleExpand={() => {
              setIsSecondaryColorExpanded(!isSecondaryColorExpanded);
            }}
          />
        )}
      </div>
    </div>
  );
};

interface LinePatterProps {}

const LinePatternControl = (props: LinePatterProps) => {
  const { project, setLinePattern, setLineDotDash } = usePosterProject();
  const { trackEvent } = useAnalyticsTracker();
  const [dotDashOption, setDotDashOption] = useState<number>(
    project.lineStyle.dotDash ? 1 : 0
  );

  useEffect(() => {
    trackEvent("Line style toggle", {
      pattern: dotDashOption === 1 ? "dotted" : "smooth",
    });
    setLineDotDash(dotDashOption === 1);
  }, [dotDashOption]);

  const icons: UIIconName[] = [
    "LINE-STYLE-SINGLE",
    "LINE-STYLE-FLANKED",
    "LINE-STYLE-TRANSLUCENT",
  ];

  const onSelectPattern = (index: number) => {
    switch (index) {
      case 0:
        setLinePattern("singleSolid");
        break;
      case 1:
        setLinePattern("doubleSolid");
        break;
      case 2:
        setLinePattern("doubleTranslucent");
        break;
    }
  };

  const indexIsSelected = (index: number) => {
    switch (index) {
      case 0:
        return project.lineStyle.mainPattern === "singleSolid";
      case 1:
        return project.lineStyle.mainPattern === "doubleSolid";
      case 2:
        return project.lineStyle.mainPattern === "doubleTranslucent";
    }
  };

  const interactiveColor = getComputedStyle(
    document.documentElement
  ).getPropertyValue("--color-interactive");

  const secondaryColor = convertToRGBA(interactiveColor, 0.5);
  return (
    <>
      <Section>
        <div className={styles.linePatternContainer}>
          {icons.map((icon, index) => (
            <UIIconButton
              key={icon}
              iconName={icon}
              isSelected={indexIsSelected(index)}
              width={55}
              height={55}
              onClick={() => onSelectPattern(index)}
              padding={0}
            />
          ))}
        </div>
      </Section>
      <Section>
        <ToolboxComponentToggle
          optionTitles={["Smooth", "Dotted"]}
          selectedOptionIndex={dotDashOption}
          onClickOption={setDotDashOption}
        />
      </Section>
    </>
  );
};

interface LineWidthProps {}
const LineWidth = (props: LineWidthProps) => {
  const { project, setLineWidth } = usePosterProject();

  return (
    <ToolboxComponentSlider
      title="Line thickness"
      min={2}
      max={16}
      value={project.lineStyle.width}
      onChange={setLineWidth}
    />
  );
};

interface LineShadowProps {}
const LineShadow = (props: LineShadowProps) => {
  const { project, setLineShadow } = usePosterProject();
  const [isColorExpanded, setIsColorExpanded] = useState<boolean>(false);
  const [showShadow, setShowShadow] = useState<boolean>(
    project.lineStyle.shadow !== undefined
  );
  const [shadow, setShadow] = useState<Shadow | undefined>(
    project.lineStyle.shadow ?? defaultShadow
  );

  useEffect(() => {
    setLineShadow(showShadow ? shadow : undefined);
  }, [showShadow]);

  const updateShadow = (style: {
    color?: string;
    offset?: number;
    blur?: number;
    opacity?: number;
    ishidden?: boolean;
  }) => {
    const newShadow: Shadow = {
      color: style.color ?? shadow?.color ?? "rgba(0, 0, 0, 0.5)",
      offset: { width: 0, height: style.offset ?? shadow?.offset.height ?? 10 },
      blur: style.blur ?? shadow?.blur ?? 7,
      opacity: style.opacity ?? shadow?.opacity ?? 0.5,
      isHidden: style.ishidden ?? shadow?.isHidden ?? false,
    };
    setShadow(newShadow);
    setLineShadow(newShadow);
  };

  return (
    <>
      <ToolboxComponentCheckbox
        text="Show shadow"
        isSelected={showShadow}
        onClick={() => {
          setShowShadow((prev) => !prev);
        }}
      />
      <ToolboxComponentColorPicker
        title="Color"
        showSwatch={true}
        colors={pickerColors}
        selectedColor={project.lineStyle.shadow?.color ?? "#000000"}
        onHoverColor={() => {}}
        onSelectColor={(color) => {
          updateShadow({ color });
        }}
        isExpanded={isColorExpanded}
        onToggleExpand={() => {
          setIsColorExpanded((prev) => !prev);
        }}
      />
      <ToolboxComponentSlider
        title="Opacity"
        min={0}
        max={100}
        value={(project.lineStyle.shadow?.opacity ?? 0.5) * 100}
        onChange={(value) => {
          updateShadow({ opacity: value / 100 });
        }}
      />
      <ToolboxComponentSlider
        title="Depth"
        min={0}
        max={100}
        value={(project.lineStyle.shadow?.offset.height ?? 7) * 7}
        onChange={(value) => {
          updateShadow({ offset: value / 7 });
        }}
      />
      <ToolboxComponentSlider
        title="Blur"
        min={0}
        max={100}
        value={(project.lineStyle.shadow?.blur ?? 7) * 5}
        onChange={(value) => {
          updateShadow({ blur: value / 5 });
        }}
      />
    </>
  );
};

const DensitySlider = () => {
  const { project, setCoordinateLimit, gpsSource } = usePosterProject();

  const onSliderChange = (value: number) => {
    setCoordinateLimit(value);
  };

  const metaText = () => {
    const limit = gpsSource?.coordinateLimit;
    const length = gpsSource?.allCoordinates.length;
    if (limit === undefined || length === undefined) {
      return "0 / 0";
    }
    return `${limit} / ${length}`;
  };

  return (
    <ToolboxComponentSlider
      title="Point density"
      meta={metaText()}
      description="Smooth out your route by adjusting the number of points"
      min={100}
      max={gpsSource?.allCoordinates.length ?? 0}
      value={
        gpsSource?.displayedCoordinates.length ??
        gpsSource?.allCoordinates.length ??
        0
      }
      onChange={onSliderChange}
    />
  );
};
